import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  ShieldCheck, Leaf, ClipboardList, AlertTriangle, CheckCircle2,
  RefreshCw, FileText, Lock, Camera, Users, Database, GraduationCap,
  Loader2, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';

const COMPLIANCE_ICONS = {
  metrc: Database,
  license: FileText,
  security: Camera,
  access: Lock,
  inventory: ClipboardList,
  audit: ShieldCheck,
  training: GraduationCap,
  data: Database,
};

const STATUS_STYLE = {
  active: { bg: 'bg-emerald-100 border-emerald-200', text: 'text-emerald-700', label: 'Active' },
  pending: { bg: 'bg-amber-100 border-amber-200', text: 'text-amber-700', label: 'Pending' },
};

export default function CannabisCompliance() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Cannabis Compliance');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: res } = await api.get('/api/remote/cannabis/compliance');
      setData(res);
    } catch { toast.error('Failed to load compliance data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  const score = data?.compliance_score || 0;
  const scoreColor = score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreRing = score >= 80 ? 'stroke-emerald-500' : score >= 50 ? 'stroke-amber-500' : 'stroke-red-500';

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="cannabis-compliance-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <Leaf className="w-6 h-6 text-emerald-600" /> Cannabis Compliance
              </h1>
              <p className="text-sm text-gray-500 mt-1">METRC tracking, license verification, and regulatory compliance</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs" data-testid="cannabis-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>

          {/* Score + Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Compliance Score Gauge */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center justify-center hover-lift" data-testid="cannabis-score-card">
              <div className="relative w-28 h-28">
                <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle cx="50" cy="50" r="42" fill="none" className={scoreRing} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${score * 2.64} ${264 - score * 2.64}`} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-2xl font-bold ${scoreColor}`} data-testid="cannabis-score-value">{score}%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 font-medium">Compliance Score</p>
            </div>

            {/* Stats */}
            <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
              <BarChart3 className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="font-heading text-2xl font-bold text-gray-900" data-testid="cannabis-total-tickets">{data?.total_tickets || 0}</p>
              <p className="text-xs text-gray-500">Cannabis Tickets</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
              <FileText className="w-5 h-5 text-emerald-600 mb-2" />
              <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="cannabis-licensed-count">{data?.licensed_count || 0}</p>
              <p className="text-xs text-emerald-600">Licensed Facilities</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
              <Users className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="font-heading text-2xl font-bold text-[#D4A843]" data-testid="cannabis-tracking-count">{Object.keys(data?.tracking_systems || {}).length}</p>
              <p className="text-xs text-[#D4A843]">Tracking Systems</p>
            </div>
          </div>

          {/* Tracking Systems Breakdown */}
          {data?.tracking_systems && Object.keys(data.tracking_systems).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Database className="w-4 h-4 text-[#D4A843]" /> Tracking System Distribution
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cannabis-tracking-systems">
                {Object.entries(data.tracking_systems).map(([name, count]) => {
                  const total = Object.values(data.tracking_systems).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={name} className="bg-gray-50 rounded-lg p-4 text-center" data-testid={`cannabis-ts-${name}`}>
                      <p className="text-lg font-bold text-gray-900">{count}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{name}</p>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className="bg-[#D4A843] rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{pct}%</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Facility Types */}
          {data?.facility_types && Object.keys(data.facility_types).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Facility Types</h3>
              <div className="flex flex-wrap gap-2" data-testid="cannabis-facility-types">
                {Object.entries(data.facility_types).map(([name, count]) => (
                  <span key={name} className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700" data-testid={`cannabis-ft-${name}`}>
                    {name}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Compliance Checklist */}
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="cannabis-compliance-checklist">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#D4A843]" /> Regulatory Compliance Checklist
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(data?.compliance_items || []).map(item => {
                const Icon = COMPLIANCE_ICONS[item.id] || ShieldCheck;
                const style = STATUS_STYLE[item.status] || STATUS_STYLE.pending;
                const isSelected = selectedItem === item.id;
                return (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${isSelected ? 'border-[#D4A843] bg-[#D4A843]/5 ring-1 ring-[#D4A843]/20' : style.bg}`}
                    onClick={() => setSelectedItem(isSelected ? null : item.id)}
                    data-testid={`cannabis-item-${item.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.status === 'active' ? 'bg-emerald-200' : 'bg-amber-200'}`}>
                        <Icon className={`w-4 h-4 ${style.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold text-gray-900">{item.name}</p>
                          {item.status === 'active'
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          }
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">{item.description}</p>
                        <span className={`inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {style.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Cannabis Tickets */}
          {(data?.recent_tickets || []).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="cannabis-recent-tickets">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[#D4A843]" /> Recent Cannabis Tickets
              </h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {data.recent_tickets.map(ticket => (
                  <div key={ticket.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`cannabis-ticket-${ticket.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${ticket.status === 'resolved' || ticket.status === 'closed' ? 'bg-emerald-500' : ticket.status === 'in_progress' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{ticket.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-gray-400">{new Date(ticket.created_at).toLocaleDateString()}</span>
                          {ticket.industry_fields?.tracking_system && (
                            <span className="text-[10px] font-mono bg-gray-200 px-1.5 py-0.5 rounded">{ticket.industry_fields.tracking_system}</span>
                          )}
                          {ticket.industry_fields?.facility_type && (
                            <span className="text-[10px] text-gray-500">{ticket.industry_fields.facility_type}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${ticket.priority === 'critical' ? 'bg-red-100 text-red-700' : ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                        {ticket.priority || 'medium'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${ticket.status === 'resolved' || ticket.status === 'closed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {ticket.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
